import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../../api/contentStudioV2';
import { useLanguage } from '../../../contexts/LanguageContext';
import SpiderRadar from '../components/SpiderRadar';
import '../styles/formErrors.css';

const Step4SpiderWeb = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { t } = useLanguage();
  const [weights, setWeights] = useState(() => {
    if (sessionData && sessionData.weights) {
      console.log('[Step4] Restoring weights:', sessionData.weights);
      return sessionData.weights;
    }
    return {
      product_core: 50,
      campaign_direction: 50,
      promotion: 50,
      ambassador_tone: 50,
      company_values: 50,
      compliance_guard: 50,
      viral_heuristics: 50,
      external_trend: 50
    };
  });

  const [aiGuidance, setAiGuidance] = useState(() => {
    if (sessionData && sessionData.aiGuidance) {
      return sessionData.aiGuidance;
    }
    return '';
  });
  
  const [hasSubmitted, setHasSubmitted] = useState(() => {
    return !!(sessionData && sessionData.weights);
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'saved', 'error', ''
  const [saveTimer, setSaveTimer] = useState(null);
  const [locale] = useState('en');

  // Presets
  const presets = {
    balanced: {
      name: 'Balanced',
      values: {
        product_core: 50,
        campaign_direction: 50,
        promotion: 50,
        ambassador_tone: 50,
        company_values: 50,
        compliance_guard: 50,
        viral_heuristics: 50,
        external_trend: 50
      }
    },
    salesPush: {
      name: 'Sales Push',
      values: {
        product_core: 80,
        campaign_direction: 45,
        promotion: 75,
        ambassador_tone: 45,
        company_values: 45,
        compliance_guard: 60,
        viral_heuristics: 45,
        external_trend: 45
      }
    },
    brandBuild: {
      name: 'Brand Build',
      values: {
        product_core: 45,
        campaign_direction: 70,
        promotion: 45,
        ambassador_tone: 70,
        company_values: 80,
        compliance_guard: 45,
        viral_heuristics: 45,
        external_trend: 45
      }
    },
    viralTest: {
      name: 'Viral Test',
      values: {
        product_core: 60,
        campaign_direction: 45,
        promotion: 45,
        ambassador_tone: 45,
        company_values: 45,
        compliance_guard: 45,
        viral_heuristics: 80,
        external_trend: 70
      }
    }
  };

  // Weight metadata (compact) - uses t() for labels
  const weightMeta = {
    product_core: {
      get label() { return t('cs.steps.spider.productCore'); },
      tooltip: 'Primary product/service focus. Higher = more USP emphasis in SOA Q3.',
      shortLabel: 'Product'
    },
    campaign_direction: {
      get label() { return t('cs.steps.spider.campaignHook'); },
      tooltip: 'Alignment with campaign strategy and positioning.',
      shortLabel: 'Direction'
    },
    promotion: {
      get label() { return t('cs.steps.spider.promotion'); },
      tooltip: 'Promotional offers. Higher = stronger CTAs in Q5.',
      shortLabel: 'Promo'
    },
    ambassador_tone: {
      get label() { return t('cs.steps.spider.ambassadors'); },
      tooltip: 'Brand ambassador personality influence.',
      shortLabel: 'Ambassador'
    },
    company_values: {
      get label() { return t('cs.steps.spider.companyValues'); },
      tooltip: 'Core values & mission. Higher = deeper emotional Q4.',
      shortLabel: 'Values'
    },
    compliance_guard: {
      get label() { return t('cs.steps.spider.complianceGuard'); },
      tooltip: 'Regulatory strictness. Higher = more cautious filters.',
      shortLabel: 'Compliance'
    },
    viral_heuristics: {
      get label() { return t('cs.steps.spider.viralHeuristics'); },
      tooltip: 'Viral potential optimization for engagement.',
      shortLabel: 'Viral'
    },
    external_trend: {
      get label() { return t('cs.steps.spider.externalTrend'); },
      tooltip: 'Market trends and timely topics incorporation.',
      shortLabel: 'Trend'
    }
  };

  // Auto-save with debounce
  const autoSave = useCallback(async (newWeights) => {
    // Clear existing timer
    if (saveTimer) {
      clearTimeout(saveTimer);
    }

    // Set new timer
    const timer = setTimeout(async () => {
      setIsSaving(true);
      setSaveStatus('');
      
      try {
        const result = await api.setSpiderWebWeights(token, sessionId, newWeights);
        setAiGuidance(result.ai_guidance);
        setHasSubmitted(true);
        setSaveStatus('saved');
        
        // Clear saved status after 2s
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (err) {
        console.error('[Step4] Auto-save failed:', err);
        setSaveStatus('error');
      } finally {
        setIsSaving(false);
      }
    }, 500);

    setSaveTimer(timer);
  }, [saveTimer, token, sessionId]);

  // Handle slider change
  const handleSliderChange = (key, value) => {
    const newWeights = {
      ...weights,
      [key]: parseInt(value)
    };
    setWeights(newWeights);
    autoSave(newWeights);
  };

  // Apply preset
  const applyPreset = (presetKey) => {
    const preset = presets[presetKey];
    if (preset) {
      setWeights(preset.values);
      autoSave(preset.values);
    }
  };

  // Reset to saved state
  const resetToSaved = () => {
    if (sessionData && sessionData.weights) {
      setWeights(sessionData.weights);
      setSaveStatus('');
    } else {
      applyPreset('balanced');
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    setError(null);

    try {
      // Ensure weights are saved
      if (!hasSubmitted || isSaving) {
        const result = await api.setSpiderWebWeights(token, sessionId, weights);
        setAiGuidance(result.ai_guidance);
        setHasSubmitted(true);
      }
      
      onComplete({ weights, aiGuidance });
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('authentication')) {
        setError('Session expired — please login again');
      } else {
        setError('Failed to save weights: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get selected products for display
  const selectedProducts = sessionData.selectedProducts || [];

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '12px' }}>
      <h2 style={{ fontSize: '18px', color: '#333', marginBottom: '8px' }}>
        {t('cs.steps.spider.title')}
      </h2>
      <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
        {t('cs.steps.spider.subtitle')}
      </p>

      {/* Save Status */}
      {saveStatus && (
        <div style={{
          padding: '6px 10px',
          borderRadius: '6px',
          fontSize: '12px',
          marginBottom: '12px',
          background: saveStatus === 'saved' ? '#E8F5E9' : '#FFEBEE',
          color: saveStatus === 'saved' ? '#2E7D32' : '#C62828',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {saveStatus === 'saved' && <span>✓</span>}
          {saveStatus === 'saved' ? 'Saved' : 'Save failed - retry'}
        </div>
      )}

      {/* Selected Packs Summary */}
      {selectedProducts.length > 0 && (
        <div style={{ 
          padding: '8px', 
          background: '#E8F4FF', 
          borderRadius: '6px', 
          marginBottom: '12px',
          fontSize: '11px',
          color: '#0e67a2'
        }}>
          <strong>{selectedProducts.length} pack{selectedProducts.length !== 1 ? 's' : ''}</strong> selected
        </div>
      )}

      {/* Presets */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>Presets:</div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {Object.keys(presets).map(key => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              style={{
                padding: '6px 10px',
                background: '#F0F0F0',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              {presets[key].name}
            </button>
          ))}
          <button
            onClick={resetToSaved}
            style={{
              padding: '6px 10px',
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '11px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            🔄 Reset
          </button>
        </div>
      </div>

      {/* Radar Chart */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px',
        background: '#FAFAFA',
        borderRadius: '8px'
      }}>
        <SpiderRadar 
          values={weights} 
          size={window.innerWidth < 768 ? Math.min(280, window.innerWidth - 60) : 380}
        />
        
        {/* Legend - 2 column compact */}
        <div style={{ 
          marginTop: '12px',
          width: '100%',
          maxWidth: '340px'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr',
            gap: '6px',
            fontSize: '11px'
          }}>
            {Object.keys(weightMeta).map((key, idx) => {
              const meta = weightMeta[key];
              const value = weights[key];
              
              return (
                <div 
                  key={key}
                  style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    background: '#fff',
                    borderRadius: '4px',
                    border: '1px solid #E0E0E0'
                  }}
                >
                  <span style={{ 
                    color: '#666',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginRight: '4px'
                  }}>
                    {meta.shortLabel}
                  </span>
                  <span style={{ 
                    color: value >= 70 ? '#F7941E' : value <= 30 ? '#999' : '#0e67a2',
                    fontWeight: 700,
                    minWidth: '28px',
                    textAlign: 'right'
                  }}>
                    {value}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Weight Sliders - Compact Grid */}
      <div style={{ 
        display: 'grid', 
        gap: '10px',
        marginBottom: '12px'
      }}>
        {Object.keys(weights).map(key => {
          const meta = weightMeta[key];
          const value = weights[key];
          
          return (
            <div key={key} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '8px',
              background: '#FAFAFA',
              borderRadius: '6px'
            }}>
              {/* Label + Value */}
              <div style={{ flex: '0 0 110px' }}>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 600, 
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {meta.label}
                  <span 
                    title={meta.tooltip}
                    style={{ 
                      fontSize: '11px', 
                      color: '#999', 
                      cursor: 'help',
                      border: '1px solid #ccc',
                      borderRadius: '50%',
                      width: '14px',
                      height: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    i
                  </span>
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 700, 
                  color: value >= 70 ? '#F7941E' : value <= 30 ? '#999' : '#0e67a2'
                }}>
                  {value}
                </div>
              </div>
              
              {/* Slider */}
              <input
                type="range"
                min="0"
                max="100"
                value={value}
                onChange={(e) => handleSliderChange(key, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    const step = e.shiftKey ? 10 : 1;
                    const newValue = e.key === 'ArrowLeft' 
                      ? Math.max(0, value - step)
                      : Math.min(100, value + step);
                    handleSliderChange(key, newValue);
                    e.preventDefault();
                  }
                }}
                aria-label={meta.label}
                aria-valuenow={value}
                aria-valuemin="0"
                aria-valuemax="100"
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  outline: 'none',
                  background: `linear-gradient(to right, #0e67a2 0%, #0e67a2 ${value}%, #E0E0E0 ${value}%, #E0E0E0 100%)`,
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  cursor: 'pointer'
                }}
              />
            </div>
          );
        })}
      </div>

      {/* AI Guidance */}
      {aiGuidance && (
        <div style={{ 
          padding: '10px', 
          background: '#FFF4E6', 
          border: '1px solid #F7941E',
          borderRadius: '6px', 
          marginBottom: '12px'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#F7941E', marginBottom: '4px' }}>
            💡 AI Guidance
          </div>
          <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
            {aiGuidance}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button 
          onClick={onBack} 
          style={{ 
            flex: 1, 
            padding: '12px', 
            background: '#fff', 
            border: '2px solid #0e67a2', 
            borderRadius: '10px', 
            color: '#0e67a2', 
            fontSize: '14px', 
            fontWeight: 600, 
            cursor: 'pointer' 
          }}
        >
          {t('cs.buttons.back')}
        </button>
        <button 
          onClick={handleContinue}
          disabled={isSaving}
          style={{ 
            flex: 2, 
            padding: '12px', 
            background: isSaving ? '#ccc' : '#F7941E', 
            border: 'none', 
            borderRadius: '10px', 
            color: '#fff', 
            fontSize: '14px', 
            fontWeight: 600, 
            cursor: isSaving ? 'not-allowed' : 'pointer' 
          }}
        >
          {isSaving ? 'Saving...' : t('cs.buttons.continue')}
        </button>
      </div>
    </div>
  );
};

export default Step4SpiderWeb;
